/**
 * BaseMetalTable Component
 * 
 * Renders the Base Metals table (QW-403) for WPS PDF documents.
 * Displays base metal specifications and thickness range qualifications.
 * 
 * Supports three welding types:
 * - Groove: Standard base metal table with backing material and retainers
 * - Overlay: Simplified table without backing/retainer sections
 * - Tubesheet: Specialized table for tube-to-tubesheet welding applications
 * 
 * Features:
 * - Material specifications (P-No, G-No, HT Condition)
 * - Thickness range qualifications (Min/Max values)
 * - As-Welded vs With PWHT conditions
 * - Backing material and retainer information (Groove only)
 * - Notes section for additional specifications
 */

import  { useMemo } from "react";

const BaseMetalTable = ({ pdf, type = "Groove" }) => {
  const pdfData = pdf[0];
  const isOverlay = type === "Overlay";
  const isTubesheet = type === "Tubesheet";
  
  const { trqType, trqData } = useMemo(() => {
    const trqType = pdfData?.wld_TRQ_Type === "Welded" ? " As-Welded" : "With PWHT";
    
    const getValue = (value) => value === "" ? "-" : value;
    
    const trqData = {
      grooveWithoutImpact: {
        min: getValue(pdfData?.WPSH_TRQ_GroovewithoutImpact_Min),
        max: getValue(pdfData?.WPSH_TRQ_GroovewithoutImpact_Max)
      },
      grooveWithImpact: {
        min: getValue(pdfData?.WPSH_TRQ_GroovewithImpact_Min),
        max: getValue(pdfData?.WPSH_TRQ_GroovewithImpact_Max)
      },
      filletWithoutImpact: {
        min: getValue(pdfData?.WPSH_TRQ_FilletwithoutImpact_Min),
        max: getValue(pdfData?.WPSH_TRQ_FilletwithoutImpact_Max)
      },
      filletWithImpact: {
        min: getValue(pdfData?.WPSH_TRQ_FilletwithImpact_Min),
        max: getValue(pdfData?.WPSH_TRQ_FilletwithImpact_Max)
      },
      baseMetalCRO: {
        min: pdfData?.WPSH_TRQ_BaseMetalCRO_Min,
        max: pdfData?.WPSH_TRQ_BaseMetalCRO_Max
      },
      baseMetalHRO: {
        min: pdfData?.WPSH_TRQ_BaseMetalHRO_Min,
        max: pdfData?.WPSH_TRQ_BaseMetalHRO_Max
      }
    };
    
    return { trqType, trqData };
  }, [pdfData]);

  const borderStyle = ".11px solid black";
  
  // Render Tubesheet table structure
  const renderTubesheetTable = () => (
    <table id="table" className="WPSTable2" style={{ width: "72%" }}>
      <tbody>
        <tr>
          <th  className="WPSnoborder" style={{ width: "14%", borderBottom: "1px solid black", borderRight: "1px solid black", backgroundColor: "white" }}></th>
          <th className="WPSleft-th"  style={{ width: "15%", borderBottom: "1px solid black", borderTop: "1px solid black", textAlign: "center" }}>Product Form</th>
          <th className="WPSleft-th"  style={{ width: "31%", borderBottom: "1px solid black", borderTop: "1px solid black", textAlign: "center" }}>Spec (Type or Grade)</th>
          <th className="WPSleft-th"  style={{ width: "10%", borderBottom: "1px solid black", borderTop: "1px solid black", textAlign: "center" }}>P No/A No</th>
          <th className="WPSleft-th" style={{ width: "7%", borderBottom: "1px solid black", borderTop: "1px solid black", textAlign: "center" }}>G No</th>
          <th className="WPSleft-th"  style={{ width: "5%", borderBottom: "1px solid black", borderTop: "1px solid black", textAlign: "center" }}>Thk.mm</th>
          <th className="WPSleft-th" style={{ width: "15%", borderBottom: borderStyle, borderTop: "1px solid black", borderRight: "1px solid black", textAlign: "center" }}>Dia.mm</th>
        </tr>
        
        <tr>
          <th style={{ borderRight: "1px solid black", borderLeft: "1px solid black" }}></th>
          <td style={{ wordBreak: "break-all", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_ProductForm}</td>
          <td style={{ wordBreak: "break-all", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_Spec}</td>
          <td style={{ wordBreak: "break-all", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_PNo}</td>
          <td style={{  wordBreak: "break-all", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_GNo}</td>
          <td style={{wordBreak: "break-all", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_Thick}</td>
          <td style={{ wordBreak: "break-all", borderRight: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_BaseMetal1_Diam}</td>
        </tr>
        
        <tr>
          <th style={{  textAlign: "Left", borderBottom: "1px solid black", borderRight: "1px solid black", borderLeft: "1px solid black" }}>Welded To</th>
          <td style={{  wordBreak: "break-all", borderBottom: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_ProductForm}</td>
          <td style={{  wordBreak: "break-all", borderBottom: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_Spec}</td>
          <td style={{  wordBreak: "break-all", borderBottom: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_PNo}</td>
          <td style={{ wordBreak: "break-all", borderBottom: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_GNo}</td>
          <td style={{ wordBreak: "break-all", borderBottom: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_Thick}</td>
          <td style={{  wordBreak: "break-all", borderBottom: "1px solid black", borderRight: "1px solid black", textAlign: "center" }}>{pdfData?.WPSH_BM_WeldBaseMetal2_Diam}</td>
        </tr>
      </tbody>
    </table>
  );
  
  const renderGrooveRows = () => (
    <>
      <tr className="WPSnoborder" style={{ borderBottom: "0", borderTop: "0" }}>
        <th className="WPStable3-thwithboder" style={{ borderBottom: "0", width: "70%" }}>
          Groove without Impact
        </th>
        <td className="WPSright-td1 dyamicwidth" style={{ borderRight: borderStyle }}>
          {trqData.grooveWithoutImpact.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: `${borderStyle} ` }}>
          {trqData.grooveWithoutImpact.max}
        </td>
      </tr>
      
      <tr className="WPSnoborder" style={{ borderBottom: "0", borderTop: "0" }}>
        <th className="WPStable3-thwithboder" style={{ borderBottom: "0", width: "65%", borderTop: "0" }}>
          Groove with Impact
        </th>
        <td className="WPSright-td1 dyamicwidth" style={{ borderRight: borderStyle, borderBottom: "0" }}>
          {trqData.grooveWithImpact.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: `${borderStyle} `, borderBottom: "0" }}>
          {trqData.grooveWithImpact.max}
        </td>
      </tr>
      
      <tr>
        <th className="WPStable3-th-left" style={{ borderBottom: "0", borderTop: "0" }}>
          Fillet without Impact
        </th>
        <td style={{ borderRight: borderStyle }}>
          {trqData.filletWithoutImpact.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: borderStyle }}>
          {trqData.filletWithoutImpact.max}
        </td>
      </tr>
      
      <tr>
        <th className="WPStable3-thwithbtboder" style={{ borderTop: "0" }}>
          Fillet with Impact
        </th>
        <td className="WPSright-td1" style={{ borderRight: borderStyle, borderBottom: borderStyle }}>
          {trqData.filletWithImpact.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: borderStyle, borderBottom: borderStyle }}>
          {trqData.filletWithImpact.max}
        </td>
      </tr>
    </>
  );

  const renderOverlayRows = () => (
    <>
      <tr className="WPSnoborder" style={{ borderBottom: "0", borderTop: "0" }}>
        <th className="WPStable3-thwithboder" style={{ borderBottom: "0", width: "70%" }}>
          Base Metal CRO
        </th>
        <td className="WPSright-td1 dyamicwidth" style={{ borderRight: borderStyle }}>
          {trqData.baseMetalCRO.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: `${borderStyle} ` }}>
          {trqData.baseMetalCRO.max}
        </td>
      </tr>
      
      <tr className="WPSnoborder" style={{ borderBottom: "0", borderTop: "0" }}>
        <th className="WPStable3-thwithboder" style={{ borderBottom: borderStyle, width: "65%", borderTop: "0" }}>
          Base Metal HRO
        </th>
        <td className="WPSright-td1 dyamicwidth" style={{ borderRight: borderStyle, borderBottom: borderStyle }}>
          {trqData.baseMetalHRO.min}
        </td>
        <td className="WPSright-td1" style={{ borderRight: `${borderStyle} `, borderBottom: borderStyle }}>
          {trqData.baseMetalHRO.max}
        </td>
      </tr>
    </>
  );

  // Return early for Tubesheet type
  if (isTubesheet) {
    return <div>{renderTubesheetTable()}</div>;
  }

  return (
    <div style={{ display: "flex" }}>
      <table id="table" className="WPSTable2">
        <tbody>
          <tr style={{ borderBottom: "0", borderRight: "1px solid black" }}>
            <th className="WPSnoborder1st" style={{ borderTop: "0", borderLeft: "0", background: "none" }}></th>
            <th className="widthth" style={{ borderLeft: borderStyle, borderTop: borderStyle, borderBottom: "0" }}>
              Material
            </th>
            <th className="widththpno" style={{ borderLeft: borderStyle, borderTop: borderStyle, borderBottom: "0" }}>
              P No.
            </th>
            <th className="widththGrpno" style={{ borderLeft: borderStyle, borderTop: borderStyle, borderBottom: "0", ...(isOverlay && { borderRight: borderStyle }) }}>
              G No.
            </th>
            {!isOverlay && (
              <th className="widththHtcon" style={{ borderLeft: borderStyle, borderRight: borderStyle, borderTop: borderStyle, borderBottom: "0" }}>
                HT Con.
              </th>
            )}
          </tr>
          
          <tr style={{ borderBottom: "0", borderRight: "1px solid black" }}>
            <th className="widthblank" style={{ borderLeft: borderStyle, borderTop: borderStyle, borderBottom: "0" }}>
              Type
            </th>
            <td className="WPStable2firstcolumn" style={{ borderLeft: borderStyle, borderTop: borderStyle, borderBottom: "0" }}>
              {pdfData?.WPSH_BM_Type_Type}
            </td>
            <td style={{ borderTop: borderStyle, borderLeft: borderStyle }}>
              {pdfData?.WPSH_BM_Type_PNo}
            </td>
            <td style={{ borderTop: borderStyle, borderLeft: borderStyle }}>
              {pdfData?.WPSH_BM_Type_GNo}
            </td>
            {!isOverlay && (
              <td style={{ borderRight: borderStyle, borderTop: borderStyle, borderLeft: borderStyle }}>
                {pdfData?.WPSH_BM_Type_HT_Con}
              </td>
            )}
          </tr>
          
          <tr style={{ borderRight: "1px solid black", ...(isOverlay && { borderBottom: borderStyle }) }}>
            <th style={{ borderLeft: borderStyle, width: "22%", ...(isOverlay && { borderBottom: borderStyle }) }}>
              Welded To
            </th>
            <td className="WPStable2firstcolumn" style={{ borderLeft: borderStyle, ...(isOverlay && { borderBottom: borderStyle }) }}>
              {pdfData?.WPSH_BM_WeldingTo_Type}
            </td>
            <td style={{ borderLeft: borderStyle, ...(isOverlay && { borderBottom: borderStyle }) }}>
              {pdfData?.WPSH_BM_WeldingTo_PNo}
            </td>
            <td style={{ borderLeft: borderStyle, ...(isOverlay && { borderBottom: borderStyle }) }}>
              {pdfData?.WPSH_BM_WeldingTo_GNo}
            </td>
            {!isOverlay && (
              <td style={{ borderRight: borderStyle, borderLeft: borderStyle }}>
                {pdfData?.WPSH_BM_WeldingTo_HT_Con}
              </td>
            )}
          </tr>
          
          {!isOverlay && (
            <>
              <tr className="WPStable2withbottomboder">
                <th style={{ borderLeft: borderStyle, borderBottom: borderStyle }}>
                  Backing
                </th>
                <td style={{ borderLeft: borderStyle }}>
                  {pdfData?.WPSH_BM_Backing_Backing}
                </td>
                <td style={{ borderLeft: borderStyle }}>
                  {pdfData?.WPSH_BM_Backing_PNo}
                </td>
                <td style={{ borderLeft: borderStyle }}>
                  {pdfData?.WPSH_BM_Backing_GNo}
                </td>
                <td style={{ borderRight: borderStyle, borderLeft: borderStyle }}>
                  --
                </td>
              </tr>
              
              <tr className="WPStable2bottomboder" style={{ borderBottom: "0" }}>
                <th className="WPStable2rightborder" style={{ borderLeft: borderStyle }} scope="row">
                  Backing Material
                </th>
                <td colSpan="5" className="WPStable2bottomboder" style={{ borderRight: borderStyle, borderLeft: borderStyle }}>
                  {pdfData?.WPSH_BM_Backing_Material}
                </td>
              </tr>
              
              <tr className="WPStable2bottomboder" style={{ borderBottom: "0" }}>
                <th className="WPStable2rightborder" style={{ borderLeft: borderStyle }} scope="row">
                  Retainers
                </th>
                <td colSpan="5" className="WPStable2bottomboder" style={{ borderRight: borderStyle, borderLeft: borderStyle }}>
                  {pdfData?.WPSH_BM_Retainer}
                </td>
              </tr>
            </>
          )}
          
          <tr className="WPStable2bottomboder" style={{ borderTop: "0", borderBottom: borderStyle }}>
            <th className="WPStable2rightborder table2bottomboder" style={{ borderLeft: borderStyle, borderTop: "0", borderBottom: borderStyle }} scope="row">
              Notes
            </th>
            <td colSpan={isOverlay ? "4" : "5"} className="WPStable2bottomboder" style={{ borderRight: borderStyle, borderLeft: borderStyle }}>
              {pdfData?.WPSH_BM_Notes}
            </td>
          </tr>
        </tbody>
      </table>
      
      <div className="WPSempty" style={{ width: "1%" }}></div>
      
      <div className="WPSTable3">
        <table className="WPStable-3" style={{ height: isOverlay ? "60px" : "100px" }}>
          <tr>
            <td className="WPSnoborder" rowSpan="2" style={{ borderTop: "0", borderLeft: "0", width: "4%" }}></td>
            <th colSpan="2" className="WPStable3-topth" style={{ width: "3.7%" }}>
              {trqType}
            </th>
          </tr>
          
          <tr>
            <th className="WPStable3-sideth1">Min</th>
            <th className="WPStable3-bottomth">Max</th>
          </tr>
          
          {isOverlay ? renderOverlayRows() : renderGrooveRows()}
        </table>
      </div>
    </div>
  );
};

export default BaseMetalTable;